import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface ForecastRow {
  readonly predictionId: string;
  readonly entityType: string;
  readonly predictedValue: string | null;
  readonly confidenceScore: string;
  readonly isLowConfidence: boolean;
  readonly status: string;
}

/**
 * M25 — Forecast Dashboard (§10).
 * Lists budget/demand forecasts (predictions of type FORECAST) with their
 * projected totals and confidence (R²). What-if scenarios are part of each
 * forecast payload; this board gives the headline projection at a glance.
 */
export function M25ForecastDashboardPage(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ForecastRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: ForecastRow[]; total: number }>('/v1/ai/predictions?predictionType=FORECAST', { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="forecast-dashboard">
      <h1>{t('m25.forecast.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m25.forecast.loading')}</p> : (
        <>
          <p aria-label="forecast-count">{t('m25.forecast.count', { count: total })}</p>
          <table aria-label="forecast-table">
            <thead>
              <tr><th>{t('m25.forecast.colEntity')}</th><th>{t('m25.forecast.colProjection')}</th><th>{t('m25.forecast.colConfidence')}</th><th>{t('m25.forecast.colFlag')}</th><th>{t('m25.forecast.colStatus')}</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.predictionId}>
                  <td>{r.entityType}</td>
                  <td>{r.predictedValue ?? '—'}</td>
                  <td>{decimal(r.confidenceScore)}</td>
                  <td>{r.isLowConfidence ? t('m25.forecast.lowConfidence') : t('m25.forecast.ok')}</td>
                  <td>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
